/**
 * DOS518 — Integrazione ASTUTO
 * restAdapter.ts — Connessione via API REST.
 *
 * Endpoint atteso: GET {baseUrl}/interventi/corrente
 * Risposta: JSON con struttura AstutoIntervento (o envelope { data: ... }).
 *
 * Quando CFRS pubblica la documentazione API, aggiornare solo:
 *   - ENDPOINT_PATH (path relativo)
 *   - eventuali header aggiuntivi
 */

import type { AstutoAdapter } from './baseAdapter';
import type { AstutoResult } from '../types';
import { loadAstutoConfig } from '../config';
import { parseJSON } from '../parsers/jsonParser';

// Path dell'endpoint — aggiornare quando CFRS fornisce la documentazione
const ENDPOINT_PATH = '/interventi/corrente';

export class RestAdapter implements AstutoAdapter {
  readonly name = 'API REST ASTUTO';

  async fetch(): Promise<AstutoResult> {
    const config = loadAstutoConfig();

    if (!config.restBaseUrl) {
      return { ok: false, error: 'URL base ASTUTO non configurato. Impostarlo nel pannello di configurazione.' };
    }

    const url = config.restBaseUrl.replace(/\/$/, '') + ENDPOINT_PATH;
    const timeout = config.restTimeoutMs ?? 8000;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    try {
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };
      if (config.restApiKey) {
        headers['Authorization'] = `Bearer ${config.restApiKey}`;
      }

      const res = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      clearTimeout(timer);

      if (!res.ok) {
        return {
          ok: false,
          error: `ASTUTO ha risposto con errore HTTP ${res.status} — ${res.statusText}`,
        };
      }

      const text = await res.text();
      const data = parseJSON(text);
      return { ok: true, data };
    } catch (err: any) {
      clearTimeout(timer);
      if (err?.name === 'AbortError') {
        return { ok: false, error: `Timeout: nessuna risposta da ASTUTO entro ${timeout / 1000}s. Verificare la connessione.` };
      }
      return { ok: false, error: `Errore di rete: ${err?.message ?? 'sconosciuto'}` };
    }
  }
}
